import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import {
  applicationsQuery, blockedQuery, deviceSettingsQuery, deviceSummaryQuery, historyQuery, nestedTableQuery, post,
  userApplicationsQuery, userPeriodApplicationsQuery, userPeriodSummaryQuery, userSummaryQuery,
  type App, type AppLists, type DeviceSettings, type ExplainedSpan, type Segment, type UserDevice, type UserTimes, type UserSummaryDevice,
} from '../api/productivitee'
import { ApiError } from '../api/client'
import { escapeHtml } from '../inventoree/format'
import { durationString } from '../easeedesk/format'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { NestedTable, type NestedColumn, type NestedState } from '../securitee/NestedTable'
import { localPage, type LocalColumn } from './localTable'
import { hhmm } from './columns'
import { SettingsCheck, SettingsRadio } from '../ui/SettingsParts'
import { UserEvolutionTab } from './UserEvolution'
import type { Period } from '../api/securitee'

/**
 * Randurile desfasurate din Productivitee - `getActionTabs` (masina: Summary,
 * Restrictions, My Applications, Productivity History, Events, Settings),
 * `getUserActionTabs` (utilizator pe zi: Summary, Applications activity) si
 * `getUserPeriodActionTabs` (utilizator pe perioada: aceleasi doua, pe ziua
 * `from`). Banda zilei e `productivityChart` din public/js/productivitee.js:
 * 24 de ore a cate 36.7 px, segmentele vin gata socotite de la API.
 */

export async function send(path: string, fields: Record<string, string>, done: string, onDone?: () => void): Promise<boolean> {
  try {
    await post(path, fields)
    toast(done, 'Success', 'success')
    onDone?.()
    return true
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong!', 'Error', 'error')
    return false
  }
}

function Tabs({ tabs, children }: { tabs: { key: string; label: string }[]; children(tab: string): React.ReactNode }) {
  const [tab, setTab] = useState(tabs[0]!.key)
  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {tabs.map((t) => (
            <li key={t.key}>
              <a href="#" className={`action_button${tab === t.key ? ' active' : ''}`} onClick={(e) => { e.preventDefault(); setTab(t.key) }}>{t.label}</a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12"><div className="details_content">{children(tab)}</div></div>
    </div>
  )
}

function Stat({ color, icon, number, label }: { color: string; icon: string; number: string | number; label: string }) {
  return (
    <div className="col-md-3">
      <div className={`dashboard-stat ${color}`}>
        <div className="visual"><i className={icon}></i></div>
        <div className="details"><div className="number number-summary">{number}</div><div className="desc"></div></div>
        <div className="more">{label}</div>
      </div>
    </div>
  )
}

/** Culorile legendei din summary.php: Productive #458AB6, Inactive #ededed, Unproductive #FFBD00. */
const SEGMENT_COLOR: Record<number, string> = { 0: '#ededed', 1: '#458AB6', 2: '#FFBD00' }

/** O ora de banda, in px, asa cum socoteste `getNewChartProductivityData` latimile. */
const HOUR_PX = 36.7

/**
 * Rama benzii (`.productivity-bars` din app.css) are `border: 4px` si
 * `padding: 0 20px`, iar foaia pune `* { box-sizing: border-box }`: latimea
 * scrisa aici e a RAMEI, nu a celor 24 de ore dinauntru. Pana pe 21 septembrie
 * 2026 sta scris `24 * 36.7`, deci ziua iesea cu 48 px in afara chenarului
 * rotunjit si scotea o bara de defilare pe o banda care incape.
 */
const FRAME_PX = 8 + 40

/** `padding-left` al ramei: acolo incepe ora 00:00 a benzii. */
const BAND_PAD_PX = 20

/** Minutele de la miezul noptii ale unei ore `HH:MM`, sau null. */
function clockMinutes(clock: string): number | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(clock)
  return m ? Number(m[1]) * 60 + Number(m[2]) : null
}

/** "HH:MM" -> px de la 00:00 pe banda; `null` cand nu e o ora. */
function clockPx(clock: string): number | null {
  const min = clockMinutes(clock)
  return min === null ? null : (min / 60) * HOUR_PX
}

/**
 * productivityChart: banda de 24 h, primul segment e golul pana la pornire (transparent).
 *
 * `explained`: golurile explicate de om in "Your day", cu mov, asezate la ora
 * lor PESTE banda (pozitie absoluta in rama), deci peste golul pe care il
 * explica - segmentele benzii raman neatinse. Pe fiecare, tooltip-ul spune
 * felul si ora de inceput si de sfarsit.
 */
export function DayBand({ chart, explained = [] }: { chart: Segment[]; explained?: ExplainedSpan[] }) {
  const spans = explained.flatMap((e, i) => {
    const from = clockPx(e.start)
    const to = clockPx(e.end)
    return from !== null && to !== null && to > from ? [{ ...e, i, left: BAND_PAD_PX + from, width: to - from }] : []
  })
  return (
    <div className="productivity-chart" style={{ overflowX: 'auto' }}>
      <div className="productivity-legend" style={{ fontSize: 11, marginBottom: 4 }}>
        <i className="fa fa-circle" style={{ color: SEGMENT_COLOR[1] }}></i> Productive - <i className="fa fa-circle" style={{ color: SEGMENT_COLOR[0] }}></i> Inactive - <i className="fa fa-circle" style={{ color: SEGMENT_COLOR[2] }}></i> Unproductive
        {spans.length > 0 && <> - <i className="fa fa-circle" style={{ color: 'var(--od-explained)' }}></i> Explained</>}
      </div>
      <div className="productivity-bars" style={{ display: 'flex', position: 'relative', width: 24 * HOUR_PX + FRAME_PX, height: 18, background: '#f5f5f5' }}>
        {chart.map(([px, type], i) => (
          <div key={i} className={`segment segment-${type}`} style={{ width: Math.max(0, px), height: '100%', background: i === 0 ? 'transparent' : SEGMENT_COLOR[type] ?? SEGMENT_COLOR[0], flex: 'none' }} title={`${type === 1 ? 'productive' : type === 2 ? 'unproductive' : 'inactive'} ${Math.round((px / HOUR_PX) * 60)} min`} />
        ))}
        {spans.map((s) => (
          <div key={`x${s.i}`} className="od-explained-span" data-kind={s.kind} style={{ left: s.left, width: s.width }} title={`${s.label} ${s.start} - ${s.end}`} />
        ))}
      </div>
      {/* summary.php numara `$i < 25`: 25 de semne, de la 00:00 la 24:00, cate
          unul la fiecare hotar de ora al benzii - nu 24, cat sunt orele. */}
      <div className="productivity-day-hours" style={{ display: 'flex', width: 25 * HOUR_PX + 8, fontSize: 9 }}>
        {Array.from({ length: 25 }, (_, i) => <div key={i} className="productivity-hour" style={{ width: HOUR_PX, flex: 'none' }}>{String(i).padStart(2, '0')}:00</div>)}
      </div>
    </div>
  )
}

/**
 * Aplicatia ramasa fara nume: catalogul, titlul ferestrei si executabilul sunt
 * toate goale (le incearca pe rand `DbalProductiviteeSource::applicationName`).
 * Atunci celula nu se lasa goala - scrie "no name" cu litere sterse, ca la
 * aparatul fara nume din `columns.ts`.
 */
function AppName({ name }: { name: string }) {
  const clean = String(name ?? '').trim()
  return clean !== '' ? <>{clean}</> : <em className="color-light-grey">no name</em>
}

/** productive_applications.php: numele, timpul, si la coada `Total time` - suma listei. */
function AppList({ apps }: { apps: App[] }) {
  if (apps.length === 0) return <p className="color-light-grey"><strong>No applications</strong></p>
  const total = apps.reduce((sum, a) => sum + (Number(a.app_time) || 0), 0)
  return (
    <table className="productivity-app-list" style={{ width: '100%', marginTop: 15 }}>
      <tbody>
        {apps.map((a, i) => <tr key={`${a.application_name}-${i}`}><td><AppName name={a.application_name} /></td><td>{durationString(a.app_time)}</td></tr>)}
        <tr className="productivity-app-total"><td colSpan={2}><strong><i className="far fa-clock"></i> Total time: {durationString(total)}</strong></td></tr>
      </tbody>
    </table>
  )
}

/**
 * devices/others/summary.php - cele trei liste de aplicatii.
 *
 * Fiecare coloana isi poarta clasa ei (`productive-applications-list` si
 * surorile): acolo sta, in `app.css`, tot ce deosebeste cele trei liste una de
 * alta - titlul albastru / chihlimbariu / gri si dungile randurilor, cu linia
 * alba dintre nume si timp. Fara ele, cele trei ies identice si scrise cu
 * negru, si singurul lucru care le deosebea ramanea cuvantul din titlu.
 *
 * Lista GOALA nu intra in tabel: ramane randul sters de deasupra
 * (`No applications`), nu o banda intreaga de culoare cat coloana, cum ii
 * iesea portalului vechi din regula de rand impar. O lista fara nimic in ea
 * nu e o atentionare.
 */
export function AppLists3({ lists, explained }: { lists: AppLists; explained?: ExplainedSpan[] }) {
  return (
    <>
    <ExplainedList explained={explained} />
    <div className="row">
      <div className="col-md-12 divider"><h4 className="bordered">Applications</h4></div>
      <div className="col-md-4"><div className="productive-applications-list"><h5><i className="fa fa-star"></i> Productive applications</h5><AppList apps={lists.productive} /></div></div>
      <div className="col-md-4"><div className="unproductive-applications-list"><h5><i className="far fa-star"></i> Unproductive applications</h5><AppList apps={lists.unproductive} /></div></div>
      <div className="col-md-4"><div className="untagged-applications-list"><h5><i className="fa fa-question-circle"></i> Untagged applications</h5><AppList apps={lists.untagged} /></div></div>
    </div>
    </>
  )
}

/**
 * EXPLAINED GAPS deasupra listelor de aplicatii (Roxana, 28 septembrie 2026):
 * cate un rand pe fiecare explicatie scrisa de om in "Your day", in acelasi fel
 * de tabel ca aplicatiile - felul si intervalul in coloana numelui, apoi
 * EXPLICATIA omului (`note`), apoi durata - cu mov, ordonate dupa ora de
 * inceput.
 *
 * `note` e text scris de om: se pune ca text, React il scapa. Fara explicatie,
 * celula ramane goala.
 *
 * Fara rand de total: explicatiile se pot suprapune, iar totalul reunit e deja
 * insigna "Explained gaps" din antet; o suma aici l-ar contrazice.
 * Fara explicatii nu se deseneaza nimic.
 */
export function ExplainedList({ explained = [] }: { explained?: ExplainedSpan[] }) {
  const rows = explained
    .flatMap((e, i) => {
      const from = clockMinutes(e.start)
      const to = clockMinutes(e.end)
      return from !== null && to !== null && to > from ? [{ ...e, i, from, seconds: (to - from) * 60 }] : []
    })
    .sort((a, b) => a.from - b.from || a.i - b.i)
  if (rows.length === 0) return null
  return (
    <div className="row">
      <div className="col-md-12">
        <div className="explained-applications-list">
          <h5><i className="fa fa-comment-dots"></i> Explained gaps</h5>
          <table className="productivity-app-list" style={{ width: '100%', marginTop: 15 }}>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.start}-${r.i}`} data-kind={r.kind}>
                  <td className="explained-kind"><strong>{r.label}</strong> <span className="explained-interval">{r.start} - {r.end}</span></td>
                  <td className="explained-note">{r.note ?? ''}</td>
                  <td className="explained-duration">{durationString(r.seconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ masina */

const DEVICE_TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'restrictions', label: 'Restrictions' },
  { key: 'applications', label: 'My Applications' },
  { key: 'history', label: 'Productivity History' },
  { key: 'events', label: 'Events' },
  { key: 'settings', label: 'Settings' },
]

/** tabList.php: numai calculatoarele (`Tip` 1, 2, 3) au si celelalte cinci file; restul au doar Summary. */
const COMPUTER_TYPES = [1, 2, 3]

export function DeviceTabs({ id, date, tip }: { id: number; date: string; tip: number }) {
  return (
    <Tabs tabs={COMPUTER_TYPES.includes(tip) ? DEVICE_TABS : DEVICE_TABS.slice(0, 1)}>
      {(tab) => (
        <>
          {tab === 'summary' && <DeviceSummaryTab id={id} date={date} />}
          {tab === 'restrictions' && <RestrictionsTab id={id} />}
          {tab === 'applications' && <ApplicationsTab id={id} />}
          {tab === 'history' && <HistoryTab id={id} date={date} />}
          {tab === 'events' && <EventsTab id={id} />}
          {tab === 'settings' && <SettingsTab id={id} />}
        </>
      )}
    </Tabs>
  )
}

/**
 * Timpii unei masini pe o zi - randul de 4 carduri Stat: Starting / Working /
 * Active / Inactive. Comun filei aparatului (Device Expand) si sectiunii
 * per-masina din Summary-ul utilizatorului, ca sa arate identic. `startingTime`
 * vine gata ca "HH:MM" de la API, timpii in secunde.
 */
function DeviceTimeCards({ startingTime, workingTime, activeTime, inactiveTime }: { startingTime: string | null; workingTime: number; activeTime: number; inactiveTime: number }) {
  return (
    <div className="row">
      <Stat color="blue-madison" icon="far fa-clock" number={startingTime ?? 'n/a'} label="Starting time" />
      <Stat color="blue-mauve" icon="fa fa-briefcase" number={durationString(workingTime)} label="Working time" />
      <Stat color="green-haze" icon="fa fa-bolt" number={durationString(activeTime)} label="Active time" />
      <Stat color="red-intense" icon="fa fa-pause" number={durationString(inactiveTime)} label="Inactive time" />
    </div>
  )
}

/** Culoarea productivitatii: sub 50% rosu, 50-90% portocaliu, peste 90% verde. */
function prodColor(p: number): string {
  return p < 50 ? '#e74c3c' : p <= 90 ? '#e67e22' : '#26a65b'
}

/**
 * "Explained gaps" in Summary: aceeasi insigna mov ca in coloana listelor,
 * langa titlul benzii. Nimic explicat = "none", sters.
 */
function ExplainedGaps({ seconds }: { seconds: number }) {
  return (
    <span className="explained-gaps" style={{ marginLeft: 8 }}>
      <i className="fa fa-comment-dots" style={{ color: 'var(--od-explained)' }} aria-hidden="true"></i> Explained gaps:{' '}
      {seconds > 0 ? <span className="od-explained-chip">{durationString(seconds)}</span> : <span className="color-light-grey">none</span>}
    </span>
  )
}

/** Office ranking e mereu albastru. */
const RANK_COLOR = '#2f80ed'

/**
 * Titlul benzii cu Productivity (colorat pe praguri) si Office ranking (albastru)
 * scrise langa el, ca in summary.php. Nu mai sunt carduri: banda sta sub propriul
 * ei titlu, ca listele de aplicatii. `title` e "Device Productivity" pe fila
 * aparatului si numele masinii in Summary-ul utilizatorului.
 */
function DeviceBandHeading({ title, productivity, ranking, explained }: { title: string; productivity: number; ranking: number; explained?: number }) {
  const pc = prodColor(productivity)
  return (
    <div className="row"><div className="col-md-12 divider"><h4 className="bordered">{title} <small style={{ marginLeft: 8 }}><i className="fa fa-chart-line" style={{ color: pc }}></i> Productivity: <span style={{ color: pc, fontWeight: 600 }}>{productivity}%</span> <i className="fa fa-trophy" style={{ color: RANK_COLOR, marginLeft: 8 }}></i> Office ranking: <span style={{ color: RANK_COLOR, fontWeight: 600 }}>{ranking}</span>{explained !== undefined && <ExplainedGaps seconds={explained} />}</small></h4></div></div>
  )
}

function DeviceSummaryTab({ id, date }: { id: number; date: string }) {
  const q = useQuery(deviceSummaryQuery(id, date))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const s = q.data
  return (
    <>
      <DeviceTimeCards startingTime={s.startingTime} workingTime={s.workingTime} activeTime={s.activeTime} inactiveTime={s.inactiveTime} />
      <DeviceBandHeading title="Device Productivity" productivity={s.productivity} ranking={s.ranking} explained={s.explainedTime} />
      <div className="row"><div className="col-md-12"><DayBand chart={s.chart} explained={s.explained} /></div></div>
      <AppLists3 lists={s.applications} explained={s.explained} />
    </>
  )
}

/** `web` = site, altfel program (restrictionsModel, history.php, blocked_applications.php). */
function typeIcon(type: unknown): string {
  return type === 'web' ? '<i class="fa fa-globe font-14 color-blue"></i>' : '<i class="fa fa-desktop font-14"></i>'
}

/**
 * Bifa Block / Unblock (`.blockApp` din unblocked_applications.php si
 * blocked_applications.php): `SettingsCheck`, nu HTML scris ca text. Tine
 * starea ei pana raspunde serverul si se intoarce singura daca scrierea pica -
 * casuta din HTML ramanea bifata si dupa o eroare.
 */
function BlockBox({ deviceId, appId, name, blocked, onDone, disabledReason }: { deviceId: number; appId: number; name: string; blocked: boolean; onDone(): void; disabledReason?: string }) {
  const [on, setOn] = useState(blocked)
  const [busy, setBusy] = useState(false)
  const change = async (block: boolean) => {
    setOn(block)
    setBusy(true)
    const ok = await send(`/productivitee/devices/${deviceId}/applications/${appId}/block`, { action: block ? '1' : '2' }, block ? 'Application was blocked.' : 'Application was unblocked.', onDone)
    setBusy(false)
    if (!ok) setOn(!block)
  }
  return <SettingsCheck className="blockApp" aria-label={`${on ? 'Unblock' : 'Block'} ${name}`} title={disabledReason} value={appId} data-device={deviceId} checked={on} disabled={busy || disabledReason !== undefined} onChange={(v) => void change(v)} />
}

/**
 * Pe Basic blocarea de web (site) e muta - agentul nu citeste titlul ferestrei,
 * deci nu stie pe ce site esti (`level` din it_productivitee_device_settings).
 * Bifa de Block pe un rand `web` e dezactivata; cea de aplicatie (exe) ramane.
 * Deblocarea (Unblock) merge oricum, deci `basic` nu atinge lista Blocked.
 */
function restrictionColumns(id: number, onDone: () => void, basic: boolean): NestedColumn[] {
  return [
    { key: 'application_type', title: 'Type', orderable: false, html: (r) => typeIcon(r.application_type) },
    { key: 'application_name', title: 'Application', html: (r) => (r.application_name ? escapeHtml(r.application_name) : 'No Name Application') },
    { key: 'ID', title: 'Block', orderable: false, html: () => '', render: (r) => <BlockBox key={String(r.ID)} deviceId={id} appId={Number(r.ID)} name={String(r.application_name ?? '')} blocked={Boolean(r.blocked)} onDone={onDone} disabledReason={basic && r.application_type === 'web' ? 'Web blocking requires the Extended level.' : undefined} /> },
  ]
}

/** devices/others/restrictions.php: Applications (neblocate, cu bifa de blocare) si Blocked (bifate, debifarea deblocheaza). */
function RestrictionsTab({ id }: { id: number }) {
  const client = useQueryClient()
  const [view, setView] = useState<'apps' | 'blocked'>('apps')
  const [state, setState] = useState<NestedState>({ start: 0, length: 10, order: { column: 1, dir: 'asc' }, search: '' })
  const refresh = () => void client.invalidateQueries({ queryKey: ['productivitee', 'devices', id] })
  const col0 = restrictionColumns(id, refresh, false)
  const col = col0[state.order.column] ?? col0[1]!
  const q = useQuery({ ...nestedTableQuery(id, 'restrictions', { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search }), placeholderData: keepPreviousData, enabled: view === 'apps' })
  const b = useQuery({ ...blockedQuery(id), enabled: view === 'blocked' })
  /* it_productivitee_device_settings.level: 1 Basic. Pe Basic web-blocking e mut. */
  const basic = (q.data?.level ?? b.data?.level) === 1
  const columns = restrictionColumns(id, refresh, basic)
  /* blocked_applications-1.js: lista vine intreaga, cu `order: [[1, 'asc']]`. */
  const [blockedState, setBlockedState] = useState<NestedState>({ start: 0, length: 10, order: { column: 1, dir: 'asc' }, search: '' })
  const blocked: LocalColumn[] = [
    { key: 'application_type', title: 'Type', orderable: false, html: (r) => typeIcon(r.application_type) },
    { key: 'application_name', title: 'Application', html: (r) => escapeHtml(r.application_name) },
    { key: 'application_id', title: 'Unblock', orderable: false, searchable: false, html: () => '', render: (r) => <BlockBox key={String(r.application_id)} deviceId={id} appId={Number(r.application_id)} name={String(r.application_name ?? '')} blocked onDone={refresh} /> },
  ]
  const blockedPage = localPage(b.data?.rows ?? [], blocked, blockedState)
  return (
    <div className="row">
      <div className="col-md-12">
        {basic && (
          <div className="alert alert-info" style={{ marginBottom: 10 }}>
            <i className="fa fa-info-circle"></i> Web (site) blocking requires the <strong>Extended</strong> level: on Basic the agent does not read the window title, so it cannot tell which site is open. Application (.exe) blocking works on Basic.
          </div>
        )}
        <ul className="nav nav-tabs managementList" style={{ marginBottom: 10 }}>
          <li className={view === 'apps' ? 'active' : undefined}><a href="#" onClick={(e) => { e.preventDefault(); setView('apps') }}><i className="fa fa-desktop"></i> Applications</a></li>
          <li className={view === 'blocked' ? 'active' : undefined}><a href="#" onClick={(e) => { e.preventDefault(); setView('blocked') }}><i className="fa fa-ban"></i> Blocked</a></li>
        </ul>
        {view === 'apps' ? (
          <NestedTable id="restrictions" columns={columns} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
        ) : b.error ? <div className="alert alert-danger">Something went wrong: {b.error.message}</div> : !b.data ? <Spinner /> : b.data.rows.length === 0 ? <p className="color-light-grey">No blocked applications.</p> : (
          <div className="restrictions-table">
            <NestedTable id="productivitee_blocked" columns={blocked} rows={blockedPage.rows} total={blockedPage.total} fetching={false} error={null} state={blockedState} onState={setBlockedState} />
          </div>
        )}
      </div>
    </div>
  )
}

/** devices/others/applications_list.php: eticheta fiecarei aplicatii in grupul masinii (changeAppTag). */
function ApplicationsTab({ id }: { id: number }) {
  const client = useQueryClient()
  const q = useQuery(applicationsQuery(id))
  /* applications_list-1.js: `order: [[1, 'asc']]`, coloana Tag nu se cauta. */
  const [state, setState] = useState<NestedState>({ start: 0, length: 10, order: { column: 1, dir: 'asc' }, search: '' })
  const group = q.data?.group ?? 0
  const columns: LocalColumn[] = [
    { key: 'application_name', title: 'Application name', html: (r) => (r.application_name ? escapeHtml(r.application_name) : '<em>n/a</em>') },
    { key: 'application_type', title: 'Type', html: (r) => escapeHtml(r.application_type) },
    {
      key: 'tag_type',
      title: 'Tag',
      titleHtml: 'Tag (<span class="color-blue">productive</span>/<span class="color-warning">unproductive</span>/<span>untagged</span>)',
      orderable: false,
      searchable: false,
      html: () => '',
      render: (r) => <>{[1, 2, 0].map((tag) => <span key={tag} style={{ marginRight: 10 }}><SettingsRadio className="appTagging" name={`tagging_${r.ID}`} data-app={r.ID} value={tag} defaultChecked={r.tag_type === tag} disabled={group === 0} label={tag === 1 ? 'productive' : tag === 2 ? 'unproductive' : 'untagged'} /></span>)}</>,
    },
  ]
  const onChange = (e: React.ChangeEvent<HTMLDivElement>) => {
    const t = e.target as HTMLInputElement
    if (t.classList.contains('appTagging')) {
      void send(`/productivitee/devices/${id}/applications/${t.getAttribute('data-app')}/tag`, { tagging: t.value }, 'Application tagged.', () => void client.invalidateQueries({ queryKey: ['productivitee', 'devices', id] }))
    }
  }
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const page = localPage(q.data.rows as unknown as Record<string, unknown>[], columns, state)
  return (
    <div className="row" onChange={onChange}>
      <div className="col-md-12">
        {group === 0 ? <div className="alert alert-warning">This device is not assigned to any group; applications cannot be tagged.</div> : null}
        <NestedTable id="productivitee_applications" columns={columns} rows={page.rows} total={page.total} fetching={false} error={null} state={state} onState={setState} />
      </div>
    </div>
  )
}

/** "2026-09-10 09:00:00" -> "09:00:00", ca `date('H:i:s', ...)` din history.php. */
function clock(value: unknown): string {
  const m = /(\d{2}:\d{2}:\d{2})$/.exec(String(value ?? ''))
  return m ? m[1]! : String(value ?? '')
}

/** history-1.js: Start si End nu se cauta, iar timpul se cauta in secunde, nu in "1h 2min". */
const HISTORY_COLUMNS: LocalColumn[] = [
  { key: 'type', title: 'Type', html: (r) => typeIcon(r.type) },
  { key: 'name', title: 'Application', html: (r) => escapeHtml(r.name) },
  { key: 'application_title', title: 'Title', html: (r) => escapeHtml(r.application_title) },
  { key: 'start_date', title: 'Start', searchable: false, html: (r) => clock(r.start_date) },
  { key: 'end_date', title: 'End', searchable: false, html: (r) => clock(r.end_date) },
  { key: 'time', title: 'Time', html: (r) => durationString(r.time) },
]

/** devices/others/history.php */
function HistoryTab({ id, date }: { id: number; date: string }) {
  const q = useQuery(historyQuery(id, date))
  /* history-1.js: `order: [[3, 'asc']]` - ziua se citeste de la prima fereastra deschisa. */
  const [state, setState] = useState<NestedState>({ start: 0, length: 10, order: { column: 3, dir: 'asc' }, search: '' })
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  if (q.data.rows.length === 0) return <p className="color-light-grey">No activity on {q.data.date}.</p>
  const page = localPage(q.data.rows, HISTORY_COLUMNS, state)
  return (
    <div className="row"><div className="col-md-12">
      <NestedTable id="productivitee_history" columns={HISTORY_COLUMNS} rows={page.rows} total={page.total} fetching={false} error={null} state={state} onState={setState} />
    </div></div>
  )
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * `moment(event_date).format("DD MMM YYYY, HH:mm")` din eventsModel: "10 Sep
 * 2026, 09:00". Se citeste din text, nu prin `new Date`: valoarea din baza e un
 * ceas de perete, iar `Date` ar muta-o dupa fusul browserului.
 */
export function eventDate(value: unknown): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(value ?? ''))
  if (!m) return escapeHtml(value)
  return `${m[3]} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}, ${m[4]}:${m[5]}`
}

const EVENT_COLUMNS: NestedColumn[] = [
  { key: 'event_date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => eventDate(r.event_date) },
  { key: 'event_message', title: 'Message', html: (r) => String(r.event_message ?? '') },
  { key: 'user_id', title: 'User', html: (r) => (r.Prenume ? escapeHtml(`${r.Prenume} ${r.Nume ?? ''}`) : '<strong>System</strong>') },
]

/**
 * events.php cerea `[[0, 'asc']]`, adica cel mai vechi eveniment sus. Aici
 * ramane `desc`: fila e un jurnal al masinii, si ce s-a intamplat ultima data
 * e motivul pentru care o deschide cineva. Diferenta e cu stiinta, nu scapata.
 */
function EventsTab({ id }: { id: number }) {
  const [state, setState] = useState<NestedState>({ start: 0, length: 10, order: { column: 0, dir: 'desc' }, search: '' })
  const col = EVENT_COLUMNS[state.order.column] ?? EVENT_COLUMNS[0]!
  const q = useQuery({ ...nestedTableQuery(id, 'events', { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search }), placeholderData: keepPreviousData })
  return (
    <div className="row"><div className="col-md-12">
      <NestedTable id="productivitee_events" columns={EVENT_COLUMNS} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
    </div></div>
  )
}

/** Capul fiecaruia din cele trei panouri ale filei Settings, ca in settings.php. */
function PanelHead({ strong, rest }: { strong: string; rest: string }) {
  return (
    <header className="panel-heading sm" data-color="theme-inverse">
      <h4><strong>{strong}</strong> {rest}</h4>
    </header>
  )
}

/** devices/others/settings.php: metoda (updateDeviceMethod), out of office (updateDeviceSettings), orele de program (updateBusinessHours). */
export function SettingsForm({ id, settings, onSaved }: { id: number; settings: DeviceSettings; onSaved(): void }) {
  return (
    <>
      <PanelHead strong="Productivity" rest="Method" />
      <form className="form-horizontal method_form" onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        void send(`/productivitee/devices/${id}/settings/method`, { method: String(new FormData(e.currentTarget).get('method') ?? '1') }, 'Productivity method saved.', onSaved)
      }}>
        <div className="form-group">
          <label className="col-md-3 control-label">Productivity method</label>
          <div className="col-md-6">
            {settings.methods.map((m) => <span key={m.ID} style={{ marginRight: 12 }}><SettingsRadio name="method" value={m.ID} defaultChecked={settings.method === m.ID} label={m.method_name} /></span>)}
          </div>
          <div className="col-md-3"><button type="submit" className="btn btn-primary save_method">Save</button></div>
        </div>
      </form>
      <PanelHead strong="Productivity" rest="Settings" />
      <form className="form-horizontal settings_form" onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const fields: Record<string, string> = {}
        if (new FormData(e.currentTarget).get('calculate-oof')) fields['calculate-oof'] = 'on'
        void send(`/productivitee/devices/${id}/settings`, fields, 'Settings saved.', onSaved)
      }}>
        <div className="form-group">
          {/* Textul de langa bifa, cuvant cu cuvant din settings.php. */}
          <label className="col-md-3 control-label">Calculate productivity when out of office</label>
          <div className="col-md-6"><SettingsCheck aria-label="Calculate productivity when out of office" name="calculate-oof" defaultChecked={settings.calculate_out_of_office} /></div>
          <div className="col-md-3"><button type="submit" className="btn btn-primary save_settings">Save</button></div>
        </div>
      </form>
      <PanelHead strong="Business" rest="Hours" />
      <form className="form-horizontal hours_form" onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const d = new FormData(e.currentTarget)
        const fields: Record<string, string> = {}
        for (const day of settings.businessHours) {
          fields[`days[${day.day_id}][start]`] = String(d.get(`days[${day.day_id}][start]`) ?? '')
          fields[`days[${day.day_id}][end]`] = String(d.get(`days[${day.day_id}][end]`) ?? '')
          fields[`days[${day.day_id}][break]`] = String(d.get(`days[${day.day_id}][break]`) ?? '0')
          if (d.get(`days[${day.day_id}][checked]`)) fields[`days[${day.day_id}][checked]`] = 'on'
        }
        void send(`/productivitee/devices/${id}/business-hours`, fields, 'Business hours saved.', onSaved)
      }}>
        <table className="table table-striped">
          <thead><tr><th>Business day</th><th>Day</th><th>From</th><th>To</th><th>Break (minutes)</th></tr></thead>
          <tbody>
            {settings.businessHours.map((day) => (
              <tr key={day.day_id}>
                <td><SettingsCheck aria-label={`${day.day_of_week} is a business day`} name={`days[${day.day_id}][checked]`} defaultChecked={day.is_business} /></td>
                <td>{day.day_of_week}</td>
                <td><input type="time" className="form-control" name={`days[${day.day_id}][start]`} defaultValue={hhmm(day.start_hour)} /></td>
                <td><input type="time" className="form-control" name={`days[${day.day_id}][end]`} defaultValue={hhmm(day.end_hour)} /></td>
                <td><input type="number" min={0} max={1440} className="form-control" name={`days[${day.day_id}][break]`} defaultValue={day.break} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="form-group"><div className="col-md-12"><button type="submit" className="btn btn-primary save_hours">Save business hours</button></div></div>
      </form>
    </>
  )
}

function SettingsTab({ id }: { id: number }) {
  const client = useQueryClient()
  const q = useQuery(deviceSettingsQuery(id))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  return <SettingsForm key={JSON.stringify(q.data)} id={id} settings={q.data} onSaved={() => void client.invalidateQueries({ queryKey: ['productivitee', 'devices', id] })} />
}

/* -------------------------------------------------------------- utilizator */

/**
 * Filele randului desfacut al unui OM. A treia, Evolution, e noua (21
 * septembrie 2026): modulul arata de cand exista numai ziua de azi, iar
 * `it_productivitee_user_summary` tine un rand pe om pe zi de luni intregi.
 * Vezi `UserEvolution.tsx` pentru de ce e fila si nu un panou in Summary.
 */
const USER_TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'applications', label: 'Applications activity' },
  { key: 'evolution', label: 'Evolution' },
]

function UserTimesRow({ times }: { times: UserTimes | null }) {
  return (
    <div className="row">
      <Stat color="blue-madison" icon="far fa-clock" number={times?.starting_time ? hhmm(times.starting_time) : 'n/a'} label="Starting time" />
      <Stat color="blue-mauve" icon="fa fa-briefcase" number={durationString(times?.working_time ?? 0)} label="Working time" />
      <Stat color="green-haze" icon="fa fa-bolt" number={durationString(times?.active_time ?? 0)} label="Active time" />
      <Stat color="red-intense" icon="fa fa-pause" number={durationString(times?.inactive_time ?? 0)} label="Inactive time" />
    </div>
  )
}

export function UserTabs({ id, date }: { id: number; date: string }) {
  return (
    <Tabs tabs={USER_TABS}>
      {(tab) => {
        if (tab === 'summary') return <UserSummaryTab id={id} date={date} />
        if (tab === 'evolution') return <UserEvolutionTab id={id} day={date} />
        return <UserApplicationsTab id={id} date={date} />
      }}
    </Tabs>
  )
}

/**
 * Un card PLAT pentru sectiunea per-masina din /user_productivity: fundal alb cu
 * o dunga colorata in stanga (accentul metricii), NU cardul plin colorat din
 * randul de sus (rezumatul pe om) sau din fila aparatului. Asa cele doua randuri
 * de carduri de pe aceeasi pagina se DEOSEBESC la prima vedere.
 */
function FlatStat({ accent, icon, number, label }: { accent: string; icon: string; number: string; label: string }) {
  return (
    <div className="col-md-3" style={{ marginBottom: 12 }}>
      <div style={{ background: '#fff', borderLeft: `4px solid ${accent}`, borderRadius: 3, boxShadow: '0 1px 2px rgba(0,0,0,0.08)', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <i className={icon} style={{ color: accent, fontSize: 20, width: 22, textAlign: 'center' }} aria-hidden="true"></i>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.1 }}>{number}</div>
          <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
        </div>
      </div>
    </div>
  )
}

/** Cele 4 timpuri ale unei masini, in cardurile plate (design nou, doar pe /user_productivity). */
function DeviceTimeCardsFlat({ startingTime, workingTime, activeTime, inactiveTime }: { startingTime: string | null; workingTime: number; activeTime: number; inactiveTime: number }) {
  return (
    <div className="row">
      <FlatStat accent="#2f80ed" icon="far fa-clock" number={startingTime ?? 'n/a'} label="Starting time" />
      <FlatStat accent="#8e44ad" icon="fa fa-briefcase" number={durationString(workingTime)} label="Working time" />
      <FlatStat accent="#26a65b" icon="fa fa-bolt" number={durationString(activeTime)} label="Active time" />
      <FlatStat accent="#e74c3c" icon="fa fa-pause" number={durationString(inactiveTime)} label="Inactive time" />
    </div>
  )
}

/**
 * Invelisul comun al unei masini care se poate DESFACE/STRANGE pe pagina omului
 * (Summary si Applications activity, ca sa arate la fel): antet cenusiu clicabil
 * cu numele masinii si, optional, ceva in dreapta (productivity/ranking); sub el,
 * continutul. Cand omul are mai multe masini pornesc stranse, cu una singura e
 * desfacuta.
 */
function DeviceCollapse({ name, account, right, defaultOpen, children }: { name: string; account?: string; right?: ReactNode; defaultOpen: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ marginBottom: 14, border: '1px solid #eee', borderRadius: 4 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ width: '100%', textAlign: 'left', background: '#fafafa', border: 'none', borderRadius: open ? '4px 4px 0 0' : 4, padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
      >
        <i className={`fa fa-chevron-${open ? 'down' : 'right'}`} style={{ color: '#999', fontSize: 12, width: 12 }} aria-hidden="true"></i>
        <strong style={{ flex: 1 }}>
          <i className="fa fa-desktop" style={{ color: '#999', marginRight: 6 }} aria-hidden="true"></i>{name}
          {account ? <span className="color-light-grey" style={{ fontWeight: 400, marginLeft: 8 }}><i className="fa fa-user" aria-hidden="true"></i> {account}</span> : null}
        </strong>
        {right}
      </button>
      {open && <div style={{ padding: '12px 14px 4px' }}>{children}</div>}
    </div>
  )
}

/**
 * Contul cu care s-a lucrat pe masina, NUMAI cand masinile omului vin din mai
 * multe conturi (randul listei aduna conturile aceluiasi proprietar de aparat):
 * atunci defalcarea spune si care cont a fost pe care masina. Cu un singur cont
 * ar repeta numele din rand, deci nu se scrie.
 */
function accountOf(devices: UserDevice[], d: UserDevice): string | undefined {
  const accounts = new Set(devices.map((x) => x.username ?? ''))
  return accounts.size > 1 && d.username ? d.username : undefined
}

/**
 * O masina din Summary-ul omului (/user_productivity): antetul are Productivity
 * colorat pe praguri si Office ranking; dedesubt cardurile plate si banda.
 */
function UserDeviceBlock({ d, defaultOpen, account, explained }: { d: UserSummaryDevice; defaultOpen: boolean; account?: string; explained?: ExplainedSpan[] }) {
  const pc = prodColor(d.productivity)
  return (
    <DeviceCollapse
      name={d.device_name}
      account={account}
      defaultOpen={defaultOpen}
      right={
        <>
          <span style={{ fontSize: 12 }}><i className="fa fa-chart-line" style={{ color: pc }} aria-hidden="true"></i> Productivity: <span style={{ color: pc, fontWeight: 600 }}>{d.productivity}%</span></span>
          <span style={{ fontSize: 12, marginLeft: 12 }}><i className="fa fa-trophy" style={{ color: RANK_COLOR }} aria-hidden="true"></i> Office ranking: <span style={{ color: RANK_COLOR, fontWeight: 600 }}>{d.ranking}</span></span>
        </>
      }
    >
      <DeviceTimeCardsFlat startingTime={d.startingTime} workingTime={d.workingTime} activeTime={d.activeTime} inactiveTime={d.inactiveTime} />
      <div className="row"><div className="col-md-12"><DayBand chart={d.chart} explained={explained} /></div></div>
    </DeviceCollapse>
  )
}

/** user/others/summary.php: orele utilizatorului, apoi fiecare masina a zilei cu rang, productivitate, timp la birou si banda. */
function UserSummaryTab({ id, date }: { id: number; date: string }) {
  const q = useQuery(userSummaryQuery(id, date))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  return (
    <>
      <UserTimesRow times={q.data.summary} />
      {/* summary.php, ca si summaryPeriod.php, isi pune masinile sub titlul asta. */}
      <div className="row"><div className="col-md-12 divider"><h4 className="bordered">User productivity on devices{q.data.explained_time !== undefined && <small><ExplainedGaps seconds={q.data.explained_time} /></small>}</h4></div></div>
      {/* DOAR aici (pe /user_productivity): fiecare masina e un bloc collapsabil,
          cu cardurile plate (alb + dunga colorata in stanga), ca sa se deosebeasca
          de randul de sus (rezumatul pe om) si de fila aparatului, care raman pe
          cardurile pline. Cu mai multe masini pornesc stranse. */}
      {q.data.devices.length === 0 ? <p className="color-light-grey">No activity on {q.data.date}.</p> : q.data.devices.map((d) => (
        <UserDeviceBlock key={d.device_id} d={d} defaultOpen={q.data!.devices.length === 1} account={accountOf(q.data!.devices, d)} explained={q.data!.explained} />
      ))}
      <ExplainedList explained={q.data.explained} />
    </>
  )
}

/** user/others/newApplications.php: listele de aplicatii ale zilei, pe fiecare masina, in blocuri collapsabile ca in Summary. */
function UserApplicationsTab({ id, date }: { id: number; date: string }) {
  const q = useQuery(userApplicationsQuery(id, date))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  if (q.data.devices.length === 0) return <p className="color-light-grey">No activity on {q.data.date}.</p>
  return (
    <>
      {q.data.devices.map((d) => (
        <DeviceCollapse key={d.device_id} name={d.device_name} account={accountOf(q.data!.devices, d)} defaultOpen={q.data!.devices.length === 1}>
          <AppLists3 lists={d.applications} />
        </DeviceCollapse>
      ))}
    </>
  )
}

/** `period` e perioada din antetul listei; fila Evolution se deschide pe ea, nu pe ziua randului. */
export function UserPeriodTabs({ id, from, period }: { id: number; from: string; period?: Period }) {
  return (
    <Tabs tabs={USER_TABS}>
      {(tab) => {
        if (tab === 'summary') return <UserPeriodSummaryTab id={id} from={from} />
        if (tab === 'evolution') return <UserEvolutionTab id={id} day={from} period={period} />
        return <UserPeriodApplicationsTab id={id} from={from} />
      }}
    </Tabs>
  )
}

/**
 * user/others/summaryPeriod.php - ACELASI Summary ca pe `/user_productivity`,
 * pentru ziua randului desfacut.
 *
 * Pana aici desena doar numele masinii si banda, fiindca schimbarea din 22
 * septembrie 2026 (cardurile filei aparatului sub fiecare masina) a prins doar
 * pagina de zi. Acelasi om, aceeasi zi, si doua Summary-uri diferite - exact
 * reclamatia Roxanei. Randul de sus (`Day ...`) ramane: pe lista de perioada
 * spune care dintre zilele omului e desfacuta.
 */
function UserPeriodSummaryTab({ id, from }: { id: number; from: string }) {
  const q = useQuery(userPeriodSummaryQuery(id, from))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  return (
    <>
      <p className="color-light-grey">Day {q.data.date}</p>
      <UserTimesRow times={q.data.summary} />
      <div className="row"><div className="col-md-12 divider"><h4 className="bordered">User productivity on devices{q.data.explained_time !== undefined && <small><ExplainedGaps seconds={q.data.explained_time} /></small>}</h4></div></div>
      {q.data.devices.length === 0 ? <p className="color-light-grey">No activity on {q.data.date}.</p> : q.data.devices.map((d) => (
        <UserDeviceBlock key={d.device_id} d={d} defaultOpen={q.data!.devices.length === 1} account={accountOf(q.data!.devices, d)} explained={q.data!.explained} />
      ))}
      <ExplainedList explained={q.data.explained} />
    </>
  )
}

/** user/others/applications.php: pe fiecare masina, etichetele grupului filtrate pe utilizator. */
function UserPeriodApplicationsTab({ id, from }: { id: number; from: string }) {
  const q = useQuery(userPeriodApplicationsQuery(id, from))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  if (q.data.devices.length === 0) return <p className="color-light-grey">No activity on {q.data.date}.</p>
  return (
    <>
      {q.data.devices.map((d) => (
        <div key={d.device_id}>
          <p><i className="fa fa-desktop"></i> {d.device_name}{accountOf(q.data!.devices, d) ? <span className="color-light-grey"> <i className="fa fa-user"></i> {accountOf(q.data!.devices, d)}</span> : null}</p>
          <AppLists3 lists={d.applications} />
        </div>
      ))}
    </>
  )
}
